import { useEffect, useRef, useState } from "react";
import { Eraser, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/** Finger / stylus / mouse signature pad. Returns a PNG data URL. */
export const SignaturePad = ({ label, signerLabel = "Name", value, onSave, onClear, testid = "sig" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const dirty = useRef(false);
  const [hasInk, setHasInk] = useState(false);
  const [name, setName] = useState(value?.signer_name || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - rect.left, y: p.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = pos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    dirty.current = true;
    if (!hasInk) setHasInk(true);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    dirty.current = false;
    setHasInk(false);
    onClear?.();
  };

  const save = async () => {
    if (!dirty.current) return;
    setBusy(true);
    try {
      await onSave?.({ image: canvasRef.current.toDataURL("image/png"), signer_name: name.trim() });
    } finally { setBusy(false); }
  };

  if (value?.file_id) {
    return (
      <div className="rounded-md border border-[#E5E7EB] bg-[#F9FAFB] p-3" data-testid={`${testid}-signed`}>
        <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">{label}</p>
        <img src={value.preview_url} alt={`${label} signature`} className="h-16 mt-1 bg-white rounded"
          data-testid={`${testid}-image`} />
        <p className="text-xs text-[#111827] mt-1">{value.signer_name || "Signed"}</p>
        <p className="text-[11px] text-[#6B7280]">
          {value.signed_at ? new Date(value.signed_at).toLocaleString() : ""}
        </p>
        <Button variant="ghost" size="sm" className="mt-1 h-7 px-2" onClick={clear}
          data-testid={`${testid}-resign`}>Sign again</Button>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white p-3" data-testid={`${testid}-pad`}>
      <div className="flex items-center gap-1.5">
        <PenLine className="h-3.5 w-3.5 text-[#002FA7]" />
        <p className="text-[10px] uppercase tracking-[0.1em] text-[#6B7280]">{label}</p>
      </div>
      <div className="relative mt-2">
        <canvas ref={canvasRef} data-testid={`${testid}-canvas`}
          className="w-full h-28 rounded border border-dashed border-[#CBD5E1] touch-none bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex flex-col justify-end pb-4 px-4"
            data-testid={`${testid}-guide`}>
            <div className="border-b border-dashed border-[#CBD5E1]" />
            <p className="mt-1 text-[11px] tracking-[0.08em] text-[#B6BFCC]">Sign here</p>
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-2 mt-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={signerLabel}
          className="h-9 flex-1 min-w-[140px]" data-testid={`${testid}-name`} />
        <Button type="button" variant="outline" size="sm" onClick={clear} className="h-9"
          data-testid={`${testid}-clear`}><Eraser className="h-3.5 w-3.5 mr-1" /> Clear</Button>
        <Button type="button" size="sm" onClick={save} disabled={busy} className="h-9 bg-[#002FA7] text-white"
          data-testid={`${testid}-save`}>Save signature</Button>
      </div>
    </div>
  );
};
